'use client';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { ScreenContainer } from 'src/components/flow';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import type { FlowScreenProps } from './types';
import { v4 as uuid } from 'uuid';
import { useSaveUserData } from 'src/utils/user-data/hooks';
import { getIsSignedIn } from 'src/redux/selectors';
import { buildResultsUrl } from 'src/redux/actions/terminate-quiz-flow';
import { useAnonymousId } from 'src/utils/analytics/hooks';
import {
  useAnalytics,
  useAnalyticsPageView,
} from '@nerdwallet-cards/analytics';
import { Routes } from 'src/lib/constants';
import { Screen } from 'src/lib/flows/screens';
import Image from 'next/image';
import { gtmPushToDataLayer } from 'src/utils/analytics/utils';

export type Auth0Analytics = {
  anonymousUserId: string;
  mostRecentTraditionalDeviceId: string | undefined;
  deployableName: string;
  deployableVersion: string;
  driverPageName: string;
  driverPageViewId: string;
  driverLocation: string;
  driverPageUrl: string;
  cancelCta?: string;
};

const REG_HERO_IMAGE =
  'https://www.nerdwallet.com/cdn/js-auth-tools/branded-elements/generic-hero-variant-full.svg';

const Auth0: React.FC<FlowScreenProps> = ({ inputs }) => {
  const dispatch = useAppDispatch();
  const formSubmitId = uuid();
  const saveUserData = useSaveUserData();
  const anonymousId = useAnonymousId();
  const isSignedIn = useAppSelector(getIsSignedIn);
  const [showPopup, setShowPopup] = useState(undefined) as [
    string | undefined,
    Dispatch<SetStateAction<string | undefined>>
  ];
  const [child, setChild] = useState(null) as [
    Window | null,
    Dispatch<SetStateAction<Window | null>>
  ];
  const { deviceId, deployableName, deployableVersion } = useAnalytics();
  const { pageViewId, pageName } = useAnalyticsPageView();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!anonymousId) return;
    const auth0Analytics: Auth0Analytics = {
      anonymousUserId: anonymousId,
      cancelCta: isMobile ? 'No, let me see my results' : undefined,
      mostRecentTraditionalDeviceId: deviceId,
      deployableName: deployableName,
      deployableVersion: deployableVersion,
      driverLocation: 'cardfinder_flow',
      driverPageName: pageName,
      driverPageViewId: pageViewId,
      driverPageUrl: `${window.location.protocol}//${window.location.host}${Routes.Flow}/${Screen.Auth0}`,
    };

    const redirectToAuth0 = async () => {
      const saveInputResponse = await saveUserData(inputs);
      const redirectAuth0Url = dispatch(
        buildResultsUrl({
          formSubmitId,
          saveInputResponse,
          auth0Analytics,
          redirectType: showPopup,
          windowType: isMobile ? 'mobile' : 'child',
        })
      );

      const params = `scrollbars=yes,resizable=yes,status=no,location=no,toolbar=no,menubar=no,width=800,height=600,left=100,top=100`;
      if (isMobile) {
        child?.close();
        window.location.assign(redirectAuth0Url);
      } else {
        setChild(
          window.open(redirectAuth0Url, 'NerdWallet Authentication', params)
        );
      }
      setShowPopup(undefined);
    };

    const redirectToLoading = async () => {
      const saveInputResponse = await saveUserData(inputs);
      const redirectToLoadingUrl = dispatch(
        buildResultsUrl({
          formSubmitId,
          saveInputResponse,
          redirectType: showPopup,
        })
      );
      child?.close();
      window.location.assign(redirectToLoadingUrl);
      setShowPopup(undefined);
    };

    if (showPopup === 'skip') {
      redirectToLoading();
    } else if (!isSignedIn && showPopup) {
      redirectToAuth0();
    }
  }, [anonymousId, showPopup, isSignedIn]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setShowPopup('signup');
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ScreenContainer>
      <Box maxWidth="md">
        <Typography
          variant="titleSmall"
          textAlign={{ xs: 'center', md: 'left' }}
          display={{ xs: 'block' }}
          marginBottom={2}
        >
          Would you like to create a NerdWallet account to save your
          recommendations?
        </Typography>
        <Typography
          variant="textLarge"
          textAlign="left"
          display={{ xs: 'none', md: 'block' }}
        >
          You can also stay on top of your credit cards, track your cashflow and
          get personalized recommendations on how you can save.
        </Typography>
      </Box>
      <Grid
        container
        marginTop={3}
        direction={{ xs: 'column-reverse', md: 'row' }}
        alignItems={'flex-start'}
      >
        <Grid item md={6} width="100%" marginBottom={5}>
          <Button
            onClick={() => setShowPopup('signup')}
            sx={(theme) => ({
              marginTop: 2,
              width: '343px',
              display: 'flex',
              [theme.breakpoints.up('sm')]: {
                alignItems: 'flex-start',
                marginLeft: 2,
              },
              [theme.breakpoints.down('sm')]: {
                zIndex: 1,
                padding: 3,
                alignItems: 'center',
                width: '100%',
              },
            })}
          >
            Sign up & see my card picks
          </Button>
          <Box marginY={3}>
            <Typography variant="textSmall">
              Already have an account?{' '}
            </Typography>
            <Link
              variant="textSmall"
              component="button"
              type="button"
              onClick={() => setShowPopup('login')}
            >
              Sign In
            </Link>
          </Box>
          <Link
            component="button"
            type="button"
            onClick={() => {
              gtmPushToDataLayer({ event: 'formcomplete' });
              setShowPopup('skip');
            }}
          >
            No, let me see my results
          </Link>
        </Grid>
        <Grid item md={6}>
          <Box
            display={{ xs: 'none', md: 'flex' }}
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: {
                display: 'flex',
                transform: 'translateY(-50px)',
              },
            })}
          >
            <Image
              unoptimized
              src={REG_HERO_IMAGE}
              alt="register hero image"
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto', maxHeight: '315px' }}
            />
          </Box>
        </Grid>
      </Grid>
    </ScreenContainer>
  );
};

export default Auth0;
